import { useEffect, useState } from "react";
import useEbayCategories from "../../data/useEbayCategories";
import LoadingWheel from "../shared/LoadingWheel";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";

interface EbayCategoryDropdownProps {
  categoryId: string | null | undefined;
  register: any;
  registerName: string;
  setValue: any;
  watch: any;
}

export function EbayStoreCategoryDropdown({ categoryId, register, registerName, setValue, watch }: EbayCategoryDropdownProps) {
  const { storeCategories, refreshStoreCategories } = useEbayCategories();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeCategories.isLoading && storeCategories.data) {
      if (!watch(registerName)) {
        setValue(registerName, storeCategories.data[0]?.categoryId);
      }

      setLoading(false);
    }
  }, [storeCategories.data, storeCategories.isLoading])

  useEffect(() => {
    if (storeCategories.isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [storeCategories.isLoading, storeCategories.data]);

  const handleCategoryChange = (registerName: string, option: any) => {
    if (option != null)
      setValue(registerName, option);
  };

  if (loading || storeCategories.isLoading || !storeCategories.data) {
    return (
      <div className="flex items-center space-x-2">
        Loading store categories...
        <span className="h-5">
          <LoadingWheel />
        </span>
      </div>
    );
  }

  const refresh = refreshStoreCategories;
  const result = refresh.data;

  // The list comes from our own table rather than eBay, so it can legitimately be empty — nobody has
  // refreshed it yet. Saying so beats an empty dropdown that looks like the shop has no categories.
  const empty = storeCategories.data.length === 0;

  return (
    <div>
      <div className="flex items-end justify-between gap-3">
        <div className="grow">
          {empty ? (
            <p className="text-sm text-gray-500">
              No store categories stored yet. Refresh to read them from eBay.
            </p>
          ) : (
            <ComboBoxInput
              key={`store-category-${categoryId}`}
              label="Store Category"
              options={storeCategories.data!}
              // The path, not the bare name: it is what eBay is sent, and two categories under
              // different parents can share a name.
              searchField={"path"}
              valueField="categoryId"
              register={register}
              setValue={handleCategoryChange}
              registerName={registerName}
              initialValue={categoryId as string | undefined}
            />
          )}
        </div>

        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isLoading}
          className="shrink-0 rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          title="Re-read the shop categories from eBay. Run this after adding, renaming or moving one."
        >
          {refresh.isLoading ? "Refreshing..." : "Refresh from eBay"}
        </button>
      </div>

      {refresh.isError && (
        <p className="mt-1 text-sm text-red-700">
          Couldn't refresh the store categories from eBay.
        </p>
      )}

      {result && (
        <p className="mt-1 text-sm text-gray-500">
          {result.total} categories from eBay ({result.added} new, {result.updated} changed)
          {result.noLongerOnEbay > 0 && `, ${result.noLongerOnEbay} no longer on eBay`}.
        </p>
      )}

      {/* These are the ones that publish with no shop category at all, so they are named rather than
          left to be found one listing at a time. */}
      {result && result.unresolvableIds.length > 0 && (
        <p className="mt-1 text-sm text-amber-700">
          {result.unresolvableIds.length} store category {result.unresolvableIds.length === 1 ? "id is" : "ids are"} in
          use that eBay doesn't have ({result.unresolvableIds.join(", ")}). Parts using{" "}
          {result.unresolvableIds.length === 1 ? "it" : "them"} will list without a store category — pick a new one on
          those categories' listing defaults.
        </p>
      )}
    </div>
  );
}
